import React, { useState, useEffect } from "react";

import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "store";
import {
  setUser,
  setUserToken,
  setMessages,
  setNotifications,
  setSentRequests,
  setReceivedRequests,
  setHostGames,
  setWhitelist,
  setBlacklist,
  setPlayerWaitlist,
  setPlayerCurrentGame,
  setPrivateCashList,
  setPrivateTourneyList,
} from "store/actions";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { CssBaseline, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { db, auth } from "config/firebase";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import {
  doc,
  collection,
  onSnapshot,
  where,
  query,
  orderBy,
  limit,
} from "firebase/firestore";

import Notifications from "sections/Notifications";
import Router from "sections/Router";
import ErrorBoundary from "./Components/ErrorBoundary";

import getUserLocation from "hooks/getUserLocation";
import getPublicGameList from "hooks/getPublicGameList";

function App() {
  const user = useSelector((state: RootState) => state.user.user);
  const theme = useTheme();
  getUserLocation();
  getPublicGameList(15);
  const claims = useSelector((state: RootState) => state.user.token);
  const [msgsCount, setMsgsCount] = useState<{ numMsgs: number; unreadMsgs: number; unreadMsg?: boolean; } | null>(null);

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    // async iife runs promises in series
    const listeners: any[] = [];
    (async function IIFE() {
      await authListener();
      if (user) {
        // ?How to store promises in array to evaluate later?
        await listeners.push(await getUnreadMsgsCount());
        await listeners.push(await getPrivateGameList());
        await listeners.push(await getMessages());
        await listeners.push(await getNotifications());
        await listeners.push(await getRequests());
        await listeners.push(await getPlayerWaitlist());
        await listeners.push(await getPlayerCurrentGame());
      }

      return () => {
        listeners.map((fn) => fn());
      };
    })();
  }, [user]); // UseEffect will only refire when the user is changed.

  useEffect(() => {
    const listeners: any[] = [];
    (async function IIFE() {
      if (claims && claims.isHost) {
        await listeners.push(await getHostGames());
        await listeners.push(await getHostLists());
      }
      return () => {
        listeners.map((fn) => fn());
      };
    })();
  }, [claims]);

  async function authListener() {
    onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        // checks to see if user email is verified
        const userToken = await getIdTokenResult(authUser);
        dispatch(setUser(authUser));
        dispatch(setUserToken(userToken.claims));
        // localStorage.setItem("user", JSON.stringify(user));
      } else {
        // User is signed out - clear Redux state
        dispatch(setUser(null));
        dispatch(setUserToken(null));
        // Clear private game lists when user logs out
        dispatch(setPrivateCashList({}));
        dispatch(setPrivateTourneyList({}));
        // localStorage.removeItem("user");
      }
    });
  }

  const getUnreadMsgsCount = () => {
    if (!user?.uid) return;
    const docRef = doc(db, `users/${user.uid}/data/messages`);
    return onSnapshot(
      docRef,
      async (docSnapshot) => {
        const data = docSnapshot.data();
        if (data) {
          setMsgsCount(data as { numMsgs: number; unreadMsgs: number; unreadMsg?: boolean; });
        }
      },
      (err) => {
        console.log(`Encountered error: ${err}`);
      },
    );
  };

  const getPlayerWaitlist = async () => {
    if (!user?.uid) return undefined;
    console.log("get player waitlist called");
    const waitlistRef = query(
      collection(db, "games"),
      where(`waitlist.${user.uid}`, "==", true),
    );

    return onSnapshot(
      waitlistRef,
      async (res) => {
        const waitlist: Record<string, any> = {};
        res.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          const { id } = data;
          waitlist[id] = data;
        });
        console.log(`waitlist`, waitlist);
        dispatch(setPlayerWaitlist(waitlist));
      },
      (err) => {
        console.log(`Encountered error: ${err}`);
      },
    );
  };

  const getPlayerCurrentGame = async () => {
    if (!user?.uid) return undefined;
    const curGameRef = query(
      collection(db, "games"),
      where(`currentPlayers.${user.uid}`, "==", true),
    );

    return onSnapshot(
      curGameRef,
      async (res) => {
        const curGame: Record<string, any> = {};
        res.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          const { id } = data;
          curGame[id] = data;
        });
        dispatch(setPlayerCurrentGame(curGame));
      },
      (err) => {
        console.log(`Encountered error: ${err}`);
      },
    );
  };

  const getPrivateGameList = async () => {
    if (!user?.uid) return;
    const privCashRef = query(
      collection(db, "games"),
      where("access", "==", "Private"),
      where("type", "==", "Cash"),
      where(`allowedPlayers.${user.uid}`, "==", true),
    );

    const privateTourneyRef = query(
      collection(db, "games"),
      where("access", "==", "Private"),
      where("type", "==", "Tournament"),
      where(`allowedPlayers.${user.uid}`, "==", true),
    );

    onSnapshot(
      privCashRef,
      async (res) => {
        const gamesList: Record<string, any> = {};
        res.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          const { id } = data;
          gamesList[id] = data;
        });
        dispatch(setPrivateCashList(gamesList));
      },
      (err) => {
        console.log(
          `Encountered error while getting private cash games: ${err}`,
        );
      },
    );

    onSnapshot(
      privateTourneyRef,
      async (res) => {
        const gamesList: Record<string, any> = {};
        res.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          const { id } = data;
          gamesList[id] = data;
        });
        dispatch(setPrivateTourneyList(gamesList));
      },
      (err) => {
        console.log(`Encountered error while getting pub tourney: ${err}`);
      },
    );
  };

  const getRequests = async () => {
    if (!user?.uid) return;
    const receivedRequestsRef = query(
      collection(db, "requests"),
      where("receiverId", "==", user.uid),
      orderBy("timeStamp", "desc"),
    );

    onSnapshot(
      receivedRequestsRef,
      async (res) => {
        const requests: Record<string, any> = {};
        console.log("res", res.docs);
        res.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          requests[data.id] = data;
        });
        dispatch(setReceivedRequests(requests));
      },
      (err) => {
        console.log(`Encountered error: ${err}`);
      },
    );

    const sentRequestsRef = query(
      collection(db, "requests"),
      where("senderId", "==", user.uid),
      orderBy("timeStamp", "desc"),
    );

    onSnapshot(
      sentRequestsRef,
      async (res) => {
        const requests: Record<string, any> = {};
        console.log("res", res.docs);
        res.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          requests[data.id] = data;
        });
        dispatch(setSentRequests(requests));
      },
      (err) => {
        console.log(`Encountered error: ${err}`);
      },
    );
  };

  const getMessages = async () => {
    if (!user?.uid) return;
    const msgRef = query(
      collection(db, "messages"),
      where("receiverId", "==", user.uid),
      orderBy("timeStamp", "desc"),
      limit(10),
    );

    if (msgRef) {
      onSnapshot(
        msgRef,
        async (res) => {
          const messages = res.docs.map((docSnapshot) => docSnapshot.data() as any);
          console.log("Messages request");
          dispatch(setMessages(messages));
        },
        (err) => {
          console.log(`Encountered error: ${err}`);
        },
      );
    }
  };

  const getNotifications = async () => {
    if (!user?.uid) return;
    const notificationRef = query(
      collection(db, "notifications"),
      where("receiverId", "==", user.uid),
      orderBy("timeStamp", "desc"),
    );

    if (notificationRef) {
      onSnapshot(
        notificationRef,
        async (res) => {
          const notifications = res.docs.map((docSnapshot) =>
            docSnapshot.data() as any,
          );
          console.log("Notifications  request");
          dispatch(setNotifications(notifications));
        },
        (err) => {
          console.log(`Encountered error: ${err}`);
        },
      );
    }
  };

  const getHostGames = async () => {
    if (!user?.uid) return;
    const hostGamesRef = query(
      collection(db, "games"),
      where("hostId", "==", user.uid),
    );
    onSnapshot(
      hostGamesRef,
      async (res) => {
        const gamesList: Record<string, any> = {};
        res.docs.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          const { id } = data;
          gamesList[id] = { ...data };
        });

        dispatch(setHostGames(gamesList));
      },
      (err) => {
        console.log(`Encountered error while getting host games: ${err}`);
      },
    );
  };

  const getHostLists = async () => {
    if (!user?.uid) return;
    const hostRef = doc(db, "hosts", user.uid);

    onSnapshot(
      hostRef,
      async (res) => {
        if (res && res.data()) {
          const data = res.data();
          dispatch(setWhitelist(data?.whitelist || {}));
          dispatch(setBlacklist(data?.blacklist || {}));
        }
      },
      () => {
        console.log(`Encountered error while getting whitelist/blacklist`);
      },
    );
  };

  return (
    <ErrorBoundary>
      <CssBaseline />
      <Box
        id="app-root"
        sx={{
          fontFamily: theme.typography.fontFamily,
          textAlign: 'center',
          fontSize: {
            xs: '70%',
            sm: '100%',
          },
          background: theme.gradients.grey,
        }}
      >
        <Notifications />
        <Router msgsCount={msgsCount} />
        <ToastContainer />
      </Box>
    </ErrorBoundary>
  );
}

export default App;
